import { formatProDocCode, PRO_DEVIS_SEQ, type DevisStatus } from '@erp/domain/pro';
import type { TypedSupabaseClient } from '../client';
import { fetchProArticleSnapshots, type ProArticleSnapshot } from './pro-article-snapshots';
import { orContainsAnyColumn } from './postgrest-search';
import { fetchAllRows } from './fetch-all-rows';
import type {
  CreateProDevisInput,
  IProDevisRepository,
  ProDevis,
  ProDevisFilters,
  ProDevisLine,
  ProDevisSummary,
  UpdateProDevisInput,
} from '../../../interfaces/src';

export class SupabaseProDevisRepository implements IProDevisRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: ProDevisFilters): Promise<ProDevis[]> {
    let query = (this.supabase as any)
      .from('pro_devis')
      .select('*, lines:pro_devis_lines(*), client:pro_clients(*)');
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.from) query = query.gte('date', filters.from);
    if (filters?.to) query = query.lte('date', filters.to);
    if (filters?.search) {
      query = query.or(orContainsAnyColumn(['code'], filters.search));
    }
    const { data, error } = await query.order('date', { ascending: false });
    if (error) throw error;
    const rows = (data ?? []) as Array<Record<string, unknown>>;
    const snapshots = await fetchProArticleSnapshots(this.supabase, collectArticleIds(rows));
    return rows.map((row) => mapDevis(row, snapshots));
  }

  async listSummaries(filters?: ProDevisFilters): Promise<ProDevisSummary[]> {
    // `code` en dernier critère de tri = ordre TOTAL, sinon `.range()` duplique
    // et saute des lignes de même date (cf. fetch-all-rows).
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let query = (this.supabase as any)
        .from('pro_devis')
        .select('code, date, status, pro_client_id, pht, pttc, total_commission');
      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.from) query = query.gte('date', filters.from);
      if (filters?.to) query = query.lte('date', filters.to);
      if (filters?.search) query = query.or(orContainsAnyColumn(['code'], filters.search));
      return query
        .order('date', { ascending: false })
        .order('code', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapDevisSummary);
  }

  async getByCode(code: string): Promise<ProDevis | null> {
    const { data, error } = await (this.supabase as any)
      .from('pro_devis')
      .select('*, lines:pro_devis_lines(*), client:pro_clients(*)')
      .eq('code', code)
      .maybeSingle();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    if (!data) return null;
    const snapshots = await fetchProArticleSnapshots(this.supabase, collectArticleIds([data]));
    return mapDevis(data, snapshots);
  }

  async create(input: CreateProDevisInput): Promise<ProDevis> {
    const code = await this.getNextCode(new Date().getFullYear());
    const { error } = await (this.supabase as any).rpc('pro_create_devis_atomic', {
      p_code: code,
      p_pro_client_id: input.proClientId ?? null,
      p_pht: input.pht,
      p_pttc: input.pttc,
      p_tva: input.tva,
      p_taux_remise: input.tauxRemise,
      p_type_remise: input.typeRemise,
      p_total_commission: input.totalCommission,
      p_lines: input.lines.map((l) => ({
        article_id: l.articleId,
        quantite: l.quantite,
        pht: l.pht,
        pttc: l.pttc,
        tva: l.tva,
        remise: l.remise,
        montant_remise: l.montantRemise,
      })),
      // Les demandes hors catalogue entrent dans la MÊME transaction que le
      // devis. `null` plutôt qu'un tableau vide : la RPC ne boucle alors pas.
      p_requests: input.productRequests?.length
        ? input.productRequests.map((r) => ({
            label: r.label,
            quantity: r.quantity,
            note: r.note ?? null,
          }))
        : null,
    });
    if (error) throw error;
    const created = await this.getByCode(code);
    if (!created) throw new Error('Devis créé mais introuvable');
    return created;
  }

  async update(code: string, input: UpdateProDevisInput): Promise<ProDevis> {
    const { error } = await (this.supabase as any).rpc('pro_update_devis_atomic', {
      p_code: code,
      p_pro_client_id: input.proClientId ?? null,
      p_pht: input.pht,
      p_pttc: input.pttc,
      p_tva: input.tva,
      p_taux_remise: input.tauxRemise,
      p_type_remise: input.typeRemise,
      p_total_commission: input.totalCommission,
      p_lines: input.lines.map((l) => ({
        article_id: l.articleId,
        quantite: l.quantite,
        pht: l.pht,
        pttc: l.pttc,
        tva: l.tva,
        remise: l.remise,
        montant_remise: l.montantRemise,
      })),
    });
    if (error) throw error;
    const fresh = await this.getByCode(code);
    if (!fresh) throw new Error('Devis introuvable après modification');
    return fresh;
  }

  async updateStatus(code: string, status: DevisStatus): Promise<ProDevis> {
    const { error } = await (this.supabase as any)
      .from('pro_devis')
      .update({ status })
      .eq('code', code);
    if (error) throw error;
    const fresh = await this.getByCode(code);
    if (!fresh) throw new Error('Devis introuvable');
    return fresh;
  }

  async getNextCode(year: number): Promise<string> {
    const { data, error } = await (this.supabase as any).rpc('get_next_pro_sequence_value', {
      p_seq_name: PRO_DEVIS_SEQ,
    });
    if (error) throw error;
    return formatProDocCode('DEV', year, Number(data));
  }
}

function collectArticleIds(rows: Array<Record<string, unknown>>): string[] {
  return rows.flatMap((row) =>
    ((row['lines'] as Array<Record<string, unknown>> | undefined) ?? [])
      .map((line) => line['article_id'] as string),
  );
}

function mapDevisLine(
  row: Record<string, unknown>,
  snapshots?: Map<string, ProArticleSnapshot>,
): ProDevisLine {
  const articleId = row['article_id'] as string;
  const snapshot = snapshots?.get(articleId);
  return {
    id: row['id'] as string,
    devisCode: row['devis_code'] as string,
    articleId,
    quantite: Number(row['quantite']),
    pht: Number(row['pht']),
    pttc: Number(row['pttc']),
    tva: Number(row['tva'] ?? 19),
    remise: Number(row['remise'] ?? 0),
    montantRemise: Number(row['montant_remise'] ?? 0),
    lineNumber: Number(row['line_number'] ?? 0),
    articleName: snapshot?.name ?? null,
    articleCode: snapshot?.code ?? null,
    articleImageUrl: snapshot?.imageUrl ?? null,
  };
}

function mapDevisSummary(row: Record<string, unknown>): ProDevisSummary {
  return {
    code: row['code'] as string,
    date: row['date'] as string,
    status: row['status'] as ProDevisSummary['status'],
    proClientId: (row['pro_client_id'] as string) ?? null,
    pht: Number(row['pht'] ?? 0),
    pttc: Number(row['pttc'] ?? 0),
    totalCommission: Number(row['total_commission'] ?? 0),
  };
}

function mapDevis(
  row: Record<string, unknown>,
  snapshots?: Map<string, ProArticleSnapshot>,
): ProDevis {
  const lines = (row['lines'] as Array<Record<string, unknown>> | undefined) ?? [];
  const client = row['client'] as Record<string, unknown> | null | undefined;
  return {
    code: row['code'] as string,
    tenantId: row['tenant_id'] as string,
    proCustomerId: row['pro_customer_id'] as string,
    proClientId: (row['pro_client_id'] as string) ?? null,
    date: row['date'] as string,
    status: row['status'] as ProDevis['status'],
    pht: Number(row['pht'] ?? 0),
    pttc: Number(row['pttc'] ?? 0),
    tva: Number(row['tva'] ?? 0),
    tauxRemise: Number(row['taux_remise'] ?? 0),
    typeRemise: row['type_remise'] as ProDevis['typeRemise'],
    totalCommission: Number(row['total_commission'] ?? 0),
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lines: lines.map((line) => mapDevisLine(line, snapshots)).sort((a, b) => a.lineNumber - b.lineNumber),
    client: client
      ? {
          id: client['id'] as string,
          tenantId: client['tenant_id'] as string,
          proCustomerId: client['pro_customer_id'] as string,
          nom: client['nom'] as string,
          prenom: (client['prenom'] as string) ?? null,
          email: (client['email'] as string) ?? null,
          telephone: (client['telephone'] as string) ?? null,
          adresse: (client['adresse'] as string) ?? null,
          ville: (client['ville'] as string) ?? null,
          cp: (client['cp'] as string) ?? null,
          complement: (client['complement'] as string) ?? null,
          depense: Number(client['depense'] ?? 0),
          commission: Number(client['commission'] ?? 0),
          soldeCumule: Number(client['solde_cumule'] ?? 0),
          notes: (client['notes'] as string) ?? null,
          createdAt: client['created_at'] as string,
          updatedAt: client['updated_at'] as string,
        }
      : null,
  };
}
